import { Link } from 'react-router-dom';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { planInfo as allPlans, planComparisonFeatures } from '@/mocks/adminPlanPermissions';
import { useUpgradeModal } from '@/context/UpgradeModalContext';

interface UpgradeModalProps {
  open: boolean;
  featureId: string;
  onClose: () => void;
}

const planOrder: Array<'free' | 'pro' | 'premium'> = ['free', 'pro', 'premium'];

const planIcon: Record<string, string> = {
  free: 'ri-radio-line',
  pro: 'ri-star-fill',
  premium: 'ri-vip-diamond-fill',
};

const planCardColors: Record<string, { border: string; bg: string; iconBg: string; iconColor: string; badge: string; badgeText: string }> = {
  free: {
    border: 'border-secondary-300',
    bg: 'bg-secondary-50',
    iconBg: 'bg-secondary-100',
    iconColor: 'text-secondary-600',
    badge: 'bg-secondary-200',
    badgeText: 'text-secondary-600',
  },
  pro: {
    border: 'border-primary-300',
    bg: 'bg-primary-50',
    iconBg: 'bg-primary-100',
    iconColor: 'text-primary-600',
    badge: 'bg-primary-200',
    badgeText: 'text-primary-600',
  },
  premium: {
    border: 'border-accent-300',
    bg: 'bg-accent-50',
    iconBg: 'bg-accent-100',
    iconColor: 'text-accent-600',
    badge: 'bg-accent-200',
    badgeText: 'text-accent-600',
  },
};

export default function UpgradeModal({ open, featureId, onClose }: UpgradeModalProps) {
  const { currentPlan, storageTotalGB, storageUsed, isFree } = usePlanPermissions();
  const { openUpgradeModal } = useUpgradeModal();

  if (!open) return null;

  const feature = featureDefinitionsCache.find((f) => f.id === featureId);

  const currentIndex = planOrder.indexOf(currentPlan.id);
  const plans = planOrder.map((id) => allPlans[id]);

  const requiredPlans = feature
    ? planOrder.slice(currentIndex + 1, planOrder.indexOf(feature.requiredPlan) + 1)
    : [];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className="relative bg-background-50 rounded-2xl w-full max-w-xl overflow-hidden shadow-2xl animate-fade-in-up max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 shrink-0">
          <div className="flex items-start justify-between mb-3">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
              <i className="ri-vip-crown-line text-amber-600 text-xl"></i>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-xl bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
            >
              <i className="ri-close-line text-foreground-500 text-sm"></i>
            </button>
          </div>

          <h3 className="text-foreground-950 font-bold text-xl leading-tight">
            Esta función requiere Pro o Premium
          </h3>

          {feature && (
            <div className="flex items-start gap-2 mt-2">
              <i className="ri-lock-line text-foreground-400 text-sm mt-0.5 shrink-0"></i>
              <p className="text-foreground-500 text-sm leading-relaxed">
                <strong className="text-foreground-700">{feature.label}</strong> — {feature.description}
              </p>
            </div>
          )}
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-6 pb-4 space-y-4 flex-1">
          {/* Current plan */}
          <div className="bg-background-100 rounded-xl p-4 border border-background-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide">
                  Tu plan actual
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                    currentPlan.id === 'free'
                      ? 'bg-secondary-100 text-secondary-600'
                      : currentPlan.id === 'pro'
                        ? 'bg-primary-100 text-primary-600'
                        : 'bg-accent-100 text-accent-600'
                  }`}>
                    {currentPlan.name}
                  </span>
                  <span className="text-foreground-500 text-xs">{currentPlan.price}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide">
                  Almacenamiento
                </p>
                <p className="text-foreground-800 text-sm font-bold mt-1">
                  {storageUsed} / {storageTotalGB} GB
                </p>
              </div>
            </div>
            <div className="h-2 bg-background-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-secondary-500 rounded-full transition-all"
                style={{
                  width: `${Math.min(
                    (parseFloat(storageUsed.replace(' GB', '')) / storageTotalGB) * 100,
                    100,
                  )}%`,
                }}
              ></div>
            </div>
          </div>

          {/* Required plan highlight */}
          {requiredPlans.length > 0 && (
            <div className="bg-background-100 rounded-xl p-4 border border-background-200">
              <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide mb-2">
                Planes necesarios
              </p>
              <div className="flex items-center gap-2">
                {requiredPlans.map((plan, i) => (
                  <span key={plan.id} className="flex items-center gap-1.5">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap ${
                      plan.id === 'pro'
                        ? 'bg-primary-100 text-primary-600'
                        : 'bg-accent-100 text-accent-600'
                    }`}>
                      <i className={`${plan.id === 'pro' ? 'ri-star-fill' : 'ri-vip-diamond-fill'} text-[9px] mr-0.5`}></i>
                      {plan.name}
                    </span>
                    {plan.id === 'premium' && (
                      <span className="px-1.5 py-0.5 rounded-md bg-accent-100 text-accent-600 text-[9px] font-bold">
                        Recomendado
                      </span>
                    )}
                    {i < requiredPlans.length - 1 && (
                      <span className="text-foreground-300 text-[11px] mx-0.5">o</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Plan comparison cards */}
          <div>
            <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide mb-2">
              Comparación rápida
            </p>
            <div className="grid grid-cols-3 gap-2">
              {plans.map((plan) => {
                const colors = planCardColors[plan.id];
                const isCurrent = plan.id === currentPlan.id;
                const isRecommended = feature && planOrder.indexOf(plan.id) >= planOrder.indexOf(feature.requiredPlan) && !isCurrent;

                return (
                  <div
                    key={plan.id}
                    className={`rounded-2xl p-4 border-2 text-center transition-all ${
                      isCurrent
                        ? `${colors.border} ${colors.bg}`
                        : isRecommended
                          ? `${colors.border} ${colors.bg}`
                          : 'border-background-200 bg-background-50'
                    }`}
                  >
                    {/* Icon */}
                    <div className={`w-10 h-10 mx-auto rounded-xl ${colors.iconBg} flex items-center justify-center mb-2`}>
                      <i className={`${planIcon[plan.id]} ${colors.iconColor} text-base`}></i>
                    </div>

                    {/* Name */}
                    <p className="text-foreground-950 font-bold text-sm mb-0.5">{plan.name}</p>
                    <p className="text-foreground-400 text-[10px] mb-2">{plan.price}</p>

                    {/* Storage highlight */}
                    <p className={`text-xs font-bold ${isCurrent ? 'text-foreground-600' : 'text-foreground-800'}`}>
                      {plan.storageGB} GB
                    </p>
                    <p className="text-foreground-400 text-[9px] mb-2">storage</p>

                    {/* Status badge */}
                    {isCurrent ? (
                      <span className={`inline-block px-2 py-0.5 rounded-full ${colors.badge} ${colors.badgeText} text-[9px] font-bold`}>
                        Actual
                      </span>
                    ) : isRecommended ? (
                      <span className={`inline-block px-2 py-0.5 rounded-full ${colors.badge} ${colors.badgeText} text-[9px] font-bold`}>
                        {plan.badge}
                      </span>
                    ) : (
                      <span className="inline-block text-foreground-300 text-[9px] font-medium">
                        —
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Feature comparison rows */}
          <div className="bg-background-100 rounded-xl p-4 border border-background-200">
            <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide mb-3">
              Qué incluye cada plan
            </p>
            <div className="space-y-2">
              {/* Header row */}
              <div className="flex items-center py-1 border-b border-background-200/70">
                <span className="flex-1 text-foreground-400 text-[10px] font-semibold"></span>
                <span className="w-16 text-center text-foreground-400 text-[9px] font-semibold">Free</span>
                <span className="w-16 text-center text-foreground-400 text-[9px] font-semibold">Pro</span>
                <span className="w-16 text-center text-foreground-400 text-[9px] font-semibold">Premium</span>
              </div>

              {planComparisonFeatures.map((row) => (
                <div key={row.label} className="flex items-center py-1.5">
                  <span className="flex-1 text-foreground-700 text-[11px] font-medium">{row.label}</span>
                  <span className={`w-16 text-center text-[11px] ${row.free === '—' ? 'text-foreground-300' : 'text-foreground-600'}`}>
                    {row.free}
                  </span>
                  <span className={`w-16 text-center text-[11px] ${row.pro === '—' ? 'text-foreground-300' : 'text-foreground-600 font-semibold'}`}>
                    {row.pro}
                  </span>
                  <span className={`w-16 text-center text-[11px] ${row.premium === '—' ? 'text-foreground-300' : 'text-accent-600 font-bold'}`}>
                    {row.premium}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA box */}
          {isFree && (
            <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl p-4 border border-primary-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-rocket-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-foreground-800 text-sm font-bold">¿Listo para crecer?</p>
                  <p className="text-foreground-500 text-[11px]">
                    Con Pro o Premium tu radio llega más lejos. Sin límites.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-background-200 flex items-center gap-3 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-xs mr-1.5"></i>
            Continuar con Free
          </button>
          <button
            onClick={() => { onClose(); openUpgradeModal(); }}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap text-center flex items-center justify-center gap-1.5"
          >
            <i className="ri-arrow-up-circle-line text-base"></i>
            Ver planes
          </button>
        </div>
      </div>
    </div>
  );
}

const featureDefinitionsCache = [
  {
    id: 'fotos',
    label: 'Galería de Fotos',
    description: 'Subí y gestioná fotos de tu radio. Requiere almacenamiento extra en tu plan.',
    requiredPlan: 'pro' as const,
  },
  {
    id: 'videos',
    label: 'Videos',
    description: 'Subí y gestioná videos. Requiere almacenamiento extra y plan Business o Premium.',
    requiredPlan: 'premium' as const,
  },
  {
    id: 'lives',
    label: 'Transmisiones en Vivo',
    description: 'Creá y programá lives. Requiere almacenamiento extra y plan Business o Premium.',
    requiredPlan: 'premium' as const,
  },
  {
    id: 'custom_domain',
    label: 'Dominio Propio',
    description: 'Vinculá tu propio dominio personalizado (ej: miradio.com).',
    requiredPlan: 'premium' as const,
  },
  {
    id: 'galerias',
    label: 'Galerías Multimedia',
    description: 'Creá galerías completas con fotos y videos.',
    requiredPlan: 'pro' as const,
  },
  {
    id: 'upload_fotos',
    label: 'Subida de Fotos',
    description: 'Subí fotos ilimitadas a tu galería.',
    requiredPlan: 'pro' as const,
  },
  {
    id: 'upload_videos',
    label: 'Subida de Videos',
    description: 'Subí videos en alta calidad.',
    requiredPlan: 'premium' as const,
  },
  {
    id: 'storage_extendido',
    label: 'Almacenamiento Extendido',
    description: 'Más espacio para tus archivos.',
    requiredPlan: 'pro' as const,
  },
  {
    id: 'storage',
    label: 'Más Storage',
    description: 'Amplía tu capacidad de almacenamiento.',
    requiredPlan: 'pro' as const,
  },
  {
    id: 'posts_con_imagenes',
    label: 'Posts con Imágenes',
    description: 'Creá publicaciones con imágenes y multimedia.',
    requiredPlan: 'pro' as const,
  },
  {
    id: 'comunidad_completa',
    label: 'Comunidad Completa',
    description: 'Acceso total a la comunidad de oyentes.',
    requiredPlan: 'pro' as const,
  },
  {
    id: 'analytics_avanzado',
    label: 'Analytics Avanzado',
    description: 'Estadísticas detalladas de audiencia y engagement.',
    requiredPlan: 'premium' as const,
  },
  {
    id: 'frecuencia_premium',
    label: 'Frecuencia Premium',
    description: 'Frecuencia personalizada para tu radio.',
    requiredPlan: 'premium' as const,
  },
  {
    id: 'soporte_prioritario',
    label: 'Soporte Prioritario',
    description: 'Atención prioritaria 24/7.',
    requiredPlan: 'premium' as const,
  },
];